<template>
  <div class="row">
    <div class="col-12 mb-5">
      <Card>
        <template #header>
          <div class="d-flex align-items-center">
            <i class="bi bi-building-check me-2"></i> Organization Details
          </div>
        </template>

        <div v-if="loading" class="text-center py-5">
          <Spinner spinner-class="mb-1" />
        </div>

        <div v-if="!loading" class="organization-details">
          <!-- Organization Header -->
          <div class="org-header p-3 mb-4 bg-light rounded-3 shadow-sm">
            <div class="row align-items-center">
              <div class="col-md-3 text-center">
                <div class="logo-container mb-3">
                  <img class="img-thumbnail bg-white shadow-sm rounded-3"
                       :src="logoUrl + organization.logo"
                       alt="Organization Logo">
                </div>
                <button v-if="profile.role !== 'user' && profile.role !== 'admin'"
                        class="btn btn-sm btn-primary mt-2 text-uppercase d-flex align-items-center mx-auto"
                        @click="$refs.refInputEl.click()">
                  <i class="bi bi-upload me-2"></i> Upload Logo
                </button>
                <p class="text-muted small mt-2">
                  Allowed: JPEG, JPG, PNG (Max: 2MB)
                </p>
                <input
                  ref="refInputEl"
                  type="file"
                  name="logo"
                  accept=".jpeg,.png,.jpg"
                  hidden
                  @input="changeLogo"
                >
              </div>
              <div class="col-md-9">
                <h1 class="display-6 fw-bold text-primary mb-3">{{ organization.name }}</h1>
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="d-flex align-items-center">
                      <i class="bi bi-geo-alt text-secondary me-2"></i>
                      <div>
                        <div class="text-muted small">Address</div>
                        <div>{{ organization.address || 'Not specified' }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex align-items-center">
                      <i class="bi bi-telephone text-secondary me-2"></i>
                      <div>
                        <div class="text-muted small">Phone</div>
                        <div>{{ organization.phone || 'Not specified' }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex align-items-center">
                      <i class="bi bi-envelope text-secondary me-2"></i>
                      <div>
                        <div class="text-muted small">Email</div>
                        <div>{{ organization.email || 'Not specified' }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex align-items-center">
                      <i class="bi bi-globe text-secondary me-2"></i>
                      <div>
                        <div class="text-muted small">Website</div>
                        <div>{{ organization.website || 'Not specified' }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Registration Information -->
          <Card class="mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="bi bi-card-checklist me-2"></i> Registration Information
              </div>
            </template>
            <div class="row g-3">
              <div class="col-md-4">
                <div>
                  <div class="text-muted small">PIN No.</div>
                  <div class="fw-medium">{{ organization.pin_no || 'Not specified' }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div>
                  <div class="text-muted small">SHIF No.</div>
                  <div class="fw-medium">{{ organization.nhif_no || 'Not specified' }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div>
                  <div class="text-muted small">NSSF No.</div>
                  <div class="fw-medium">{{ organization.nssf_no || 'Not specified' }}</div>
                </div>
              </div>
            </div>
          </Card>

          <!-- Banking Information -->
          <Card class="mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="bi bi-bank me-2"></i> Banking Information
              </div>
            </template>
            <div class="row g-3">
              <div class="col-md-4">
                <div>
                  <div class="text-muted small">Bank Name</div>
                  <div class="fw-medium">{{ organization.bank_name || 'Not specified' }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div>
                  <div class="text-muted small">Branch Name</div>
                  <div class="fw-medium">{{ organization.branch_name || 'Not specified' }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div>
                  <div class="text-muted small">Account Number</div>
                  <div class="fw-medium">{{ organization.account_no || 'Not specified' }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div>
                  <div class="text-muted small">Bank Code</div>
                  <div class="fw-medium">{{ organization.bank_code || 'Not specified' }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div>
                  <div class="text-muted small">Branch Code</div>
                  <div class="fw-medium">{{ organization.branch_code || 'Not specified' }}</div>
                </div>
              </div>
            </div>
          </Card>

          <!-- Deduction Settings -->
          <Card class="mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="bi bi-gear me-2"></i> Deduction Settings
              </div>
            </template>
            <div class="row g-3">
              <div class="col-md-4 col-lg-2">
                <div class="deduction-item d-flex align-items-center p-2 rounded">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" :checked="organization.paye" disabled>
                    <label class="form-check-label ms-2">PAYE</label>
                  </div>
                </div>
              </div>
              <div class="col-md-4 col-lg-2">
                <div class="deduction-item d-flex align-items-center p-2 rounded">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" :checked="organization.nhif" disabled>
                    <label class="form-check-label ms-2">SHIF</label>
                  </div>
                </div>
              </div>
              <div class="col-md-4 col-lg-2">
                <div class="deduction-item d-flex align-items-center p-2 rounded">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" :checked="organization.nssf" disabled>
                    <label class="form-check-label ms-2">NSSF</label>
                  </div>
                </div>
              </div>
              <div class="col-md-4 col-lg-2">
                <div class="deduction-item d-flex align-items-center p-2 rounded">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" :checked="organization.housing" disabled>
                    <label class="form-check-label ms-2">Housing Levy</label>
                  </div>
                </div>
              </div>
              <div class="col-md-4 col-lg-2">
                <div class="deduction-item d-flex align-items-center p-2 rounded">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" :checked="organization.nita" disabled>
                    <label class="form-check-label ms-2">NITA</label>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <!-- Subscription Information -->
          <Card class="mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="bi bi-calendar-check me-2"></i> Subscription Details
              </div>
            </template>
            <div class="row g-3">
              <div class="col-md-3">
                <div>
                  <div class="text-muted small">Package</div>
                  <div class="fw-medium">{{ subscription.package.name }}</div>
                </div>
              </div>
              <div class="col-md-3">
                <div>
                  <div class="text-muted small">Employees</div>
                  <div class="fw-medium">{{ subscription.employees > 900 ? 'Unlimited' : subscription.employees }}</div>
                </div>
              </div>
              <div class="col-md-3">
                <div>
                  <div class="text-muted small">Price</div>
                  <div class="fw-medium">{{ formatNumber(subscription.price) + '/month' }}</div>
                </div>
              </div>
              <div class="col-md-3">
                <div>
                  <div class="text-muted small">Period</div>
                  <div class="fw-medium">{{ formatDate(subscription.from) }} - {{ formatDate(subscription.to) }}</div>
                </div>
              </div>
            </div>
          </Card>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-center mt-4">
            <button class="btn btn-dark text-uppercase me-4 d-flex align-items-center" @click="$router.go(-1)">
              <i class="bi bi-arrow-left me-2"></i> Back
            </button>
            <button v-if="profile.role !== 'user' && profile.role !== 'admin'"
                    class="btn btn-primary text-uppercase d-flex align-items-center"
                    @click="showModal = true">
              <i class="bi bi-pencil-square me-2"></i> Edit
            </button>
          </div>
        </div>
      </Card>
    </div>
  </div>

    <!-- Edit Organization Modal -->
    <Modal :open="showModal" @closed="showModal = false" dialog-size="modal-xl">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0 d-flex align-items-center">
          <i class="bi bi-pencil-square me-2"></i> Edit Organization
        </h5>
        <button @click="showModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <!-- Basic Information -->
          <div class="card mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0 d-flex align-items-center">
                <i class="bi bi-info-circle me-2"></i> Basic Information
              </h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">Organization Name</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-building"></i></span>
                    <input type="text" class="form-control" v-model="organization.name">
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Address</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-geo-alt"></i></span>
                    <input type="text" class="form-control" v-model="organization.address">
                  </div>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Employee Prefix</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-hash"></i></span>
                    <input type="text" class="form-control" v-model="organization.employee_prefix">
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Phone</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-telephone"></i></span>
                    <input type="text" class="form-control" v-model="organization.phone">
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Email</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-envelope"></i></span>
                    <input type="email" class="form-control" v-model="organization.email">
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Website <span class="text-muted small">(optional)</span></label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-globe"></i></span>
                    <input type="url" class="form-control" v-model="organization.website">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Registration Information -->
          <div class="card mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0 d-flex align-items-center">
                <i class="bi bi-card-checklist me-2"></i> Registration Information
              </h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">PIN No.</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-upc"></i></span>
                    <input type="text" class="form-control" v-model="organization.pin_no">
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">SHIF No. <span class="text-muted small">(optional)</span></label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-shield"></i></span>
                    <input type="text" class="form-control" v-model="organization.nhif_no">
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">NSSF No. <span class="text-muted small">(optional)</span></label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-shield-check"></i></span>
                    <input type="text" class="form-control" v-model="organization.nssf_no">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Banking Information -->
          <div class="card mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0 d-flex align-items-center">
                <i class="bi bi-bank me-2"></i> Banking Information
              </h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">Bank Name <span class="text-muted small">(optional)</span></label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-building"></i></span>
                    <input type="text" class="form-control" list="bankOptions" id="bankName"
                           v-model="organization.bank_name" @change="clearBranch" placeholder="Type to search...">
                    <datalist id="bankOptions">
                      <option v-for="bank in bankNames.banks" :key="bank" :value="bank"></option>
                    </datalist>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Branch Name <span class="text-muted small">(optional)</span></label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-geo"></i></span>
                    <input type="text" class="form-control" list="branchOptions" id="branchName"
                           v-model="organization.branch_name" placeholder="Type to search...">
                    <datalist id="branchOptions">
                      <option v-for="branch in bankBranches" :key="branch" :value="branch"></option>
                    </datalist>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Account Number <span class="text-muted small">(optional)</span></label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-credit-card"></i></span>
                    <input type="text" class="form-control" placeholder="Account number"
                           v-model="organization.account_no">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Deduction Settings -->
          <div class="card mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0 d-flex align-items-center">
                <i class="bi bi-gear me-2"></i> Deduction Settings
              </h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4 col-lg-2">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="payeSwitch" v-model="organization.paye">
                    <label class="form-check-label" for="payeSwitch">Deduct PAYE</label>
                  </div>
                </div>
                <div class="col-md-4 col-lg-2">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="nhifSwitch" v-model="organization.nhif">
                    <label class="form-check-label" for="nhifSwitch">Deduct SHIF</label>
                  </div>
                </div>
                <div class="col-md-4 col-lg-2">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="nssfSwitch" v-model="organization.nssf">
                    <label class="form-check-label" for="nssfSwitch">Deduct NSSF</label>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="housingSwitch" v-model="organization.housing">
                    <label class="form-check-label" for="housingSwitch">Deduct Housing Levy</label>
                  </div>
                </div>
                <div class="col-md-4 col-lg-2">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="nitaSwitch" v-model="organization.nita">
                    <label class="form-check-label" for="nitaSwitch">Deduct NITA</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-dark text-uppercase d-flex align-items-center"
                data-dismiss="modal" @click="showModal = false">
          <i class="bi bi-x-circle me-2"></i> Cancel
        </button>
        <button v-if="profile.role !== 'user' && profile.role !== 'admin'"
                type="button" class="btn btn-primary text-uppercase d-flex align-items-center"
                @click="submitForm">
          <i class="bi bi-save me-2"></i> Save Changes
        </button>
      </div>
    </Modal>
    <!-- Edit Organization Modal End -->

</template>

<script>
import organizationService from "@/api/organizationService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import formatNumber from "../../utils/formatNumber";
import bankService from "@/api/bankService";
import loadingButton from "@/utils/loadingButton";

export default {
  name: "Organization",

  data() {
    return {
      profile: {},
      loading: true,
      showModal: false,
      organization: {},
      subscription: {
        package: {}
      },
      bankNames: {
        banks: [],
        branches: {}
      },
      bankBranches: [],
      logoUrl: process.env.VUE_APP_API_IMAGE_URL + 'organization/',
    }
  },

  methods: {
    formatNumber,
    formatDate,
    async getOrganization() {
      this.organization = await organizationService.getOrganization()
      this.loading = false
      this.organization.paye = !!this.organization.paye
      this.organization.nhif = !!this.organization.nhif
      this.organization.nssf = !!this.organization.nssf
      this.organization.housing = !!this.organization.housing
      this.organization.nita = !!this.organization.nita
    },
    submitForm: async function (e) {
      loadingButton(e)
      let response = await organizationService.updateOrganization(this.organization)
      if (response)
        this.showModal = false
        await this.getOrganization()
      loadingButton(e, false)
    },
    changeLogo : async function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      let response = false
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string')
            response = await organizationService.updateOrganizationLogo({ file: fileReader.result })
            if (response)
              await this.getOrganization()
        }
      }
    },
    clearBranch: function () {
      this.organization.branch_name = ''
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getOrganization()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
    this.bankNames = await bankService.getBankNames()
    if (this.organization.bank_name !== null) {
      this.bankBranches = this.bankNames.branches[this.organization.bank_name]
    }
  },

  watch: {
    'organization.bank_name': function () {
      this.bankBranches = this.bankNames.branches[this.organization.bank_name]
    }
  }
}
</script>

<style scoped>
.organization-details .logo-container {
  max-width: 190px;
  margin: 0 auto;
}

.organization-details .logo-container img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.deduction-item {
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.deduction-item:hover {
  background-color: #e9ecef;
}

.card-header {
  border-bottom: 2px solid rgba(13, 64, 114, 0.1);
}

.modal-header, .modal-footer {
  background-color: #f8f9fa;
}

.input-group-text {
  background-color: #f8f9fa;
  color: #0d4072;
}

.form-check-input:checked {
  background-color: #0d4072;
  border-color: #0d4072;
}
</style>
